import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Scales its content down (never up) so it fits the window height, for the projector: no scrolling when a
 * banner and the board are on screen at the same time. The width is compensated so the layout stays full width.
 */
export function FitToScreen({ children, padding = 0 }: { children: React.ReactNode; padding?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const available = window.innerHeight - padding;
      const needed = el.scrollHeight;
      setScale(needed > available && needed > 0 ? Math.max(0.5, available / needed) : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [padding]);

  return (
    <div style={{ height: scale < 1 ? `${window.innerHeight - padding}px` : undefined, overflow: scale < 1 ? 'hidden' : undefined }}>
      <div ref={ref} style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: `${100 / scale}%` }}>
        {children}
      </div>
    </div>
  );
}
